import promotionDispatch from './action';
import ModalAddOrEditPromoCode from './component/ModalAddOrEditPromoCode';
import { TYPE_MODAL, COLUMN_PROMOTION } from './constants';
import {
  CloseCircle,
  CreateNewIcon,
  DeleteModalIcon,
  EditIcon,
  NoDataQueueClosureIcon,
} from '@/assets/svg';
import { momentFormatted } from '@/helpers';
import TableCellFilted from '@/module/common/componentUI/TableCellFilted';
import CustomMenu from '@/new-components/CustomMenu';
import CustomMenuActions from '@/new-components/CustomMenuActions';
import ModalConfirmation from '@/new-components/CustomModal/ModalConfirmation';
import customToast from '@/new-components/CustomNotification';
import CustomPaperContainer from '@/new-components/CustomPaperContainer';
import CustomHeader from '@/new-components/CustomPaperContainer/CustomHeader';
import CustomTable from '@/new-components/CustomTable';
import CustomTooltip from '@/new-components/CustomTooltip';
import {
  Button,
  Drawer,
  IconButton,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { MoreVertRounded } from '@material-ui/icons';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const PROMOTION_TYPE = {
  COUNSELING: 'counselling',
  TELECONSULT: 'teleconsult',
};

const Promotion = ({ type }) => {
  const location = useLocation();
  const { list: data, paging } = useSelector((state) => state.promotion);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [typeModal, setTypeModal] = useState(null);

  const [searchKey, setSearchKey] = useState('');

  const [showDeleteModal, setDeleteShowModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const promotionType = type || location?.state?.type || 'default';
  const isCounselling = promotionType === PROMOTION_TYPE.COUNSELING;

  const getTitle = () => {
    switch (promotionType) {
      case PROMOTION_TYPE.COUNSELING:
        return 'Company Code';

      default:
        return 'Promo Code';
    }
  };

  const onShowModal = (type) => {
    setShowModal(true);
    setTypeModal(type);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setTypeModal(null);
  };

  const onDeletePromoCode = () => {
    promotionDispatch.deletePromoCode(selectedItem.id, () => {
      fetchData();
      setDeleteShowModal(false);
      customToast(
        'error',
        `${
          isCounselling ? 'Company code' : 'Promo code'
        } has been deleted successfully.`,
        isCounselling ? 'Company code' : 'Promo code'
      );
    });
  };

  const handleSubmitPromoCode = (formValue) => {
    setLoading(true);
    if (typeModal === TYPE_MODAL.Create) {
      promotionDispatch.createPromoCode(
        formValue,
        () => {
          fetchData();
          setShowModal(false);
          setTypeModal(null);
          setLoading(false);
          customToast(
            'success',
            <span>
              Promo code{' '}
              <strong style={{ fontWeight: 600 }}>{formValue.name}</strong> has
              been successully created.
            </span>,
            'New promo code created'
          );
        },
        () => {
          setLoading(false);
        }
      );
    } else if (typeModal === TYPE_MODAL.Edit) {
      promotionDispatch.editPromoCode(
        selectedItem.id,
        formValue,
        () => {
          fetchData();
          setShowModal(false);
          setTypeModal(null);
          setLoading(false);
          customToast(
            'success',
            <span>
              Promo code{' '}
              <strong style={{ fontWeight: 600 }}>{formValue.name}</strong> has
              been successully updated.
            </span>,
            'Promo code updated'
          );
        },
        () => {
          setLoading(false);
        }
      );
    }
  };

  const fetchData = async (
    search = searchKey,
    pagingData = paging,
    promoCodeType = isCounselling ? 1 : 0
  ) => {
    promotionDispatch.getData(search, pagingData, promoCodeType);
  };

  const debounceLoadData = useCallback(
    debounce((searchKey, paging) => {
      fetchData(searchKey, {
        pageIndex: 0,
        pageSize: paging.pageSize,
        promoCodeType: isCounselling ? 1 : 0,
      });
    }, 500),
    []
  );

  useEffect(() => {
    debounceLoadData(searchKey, paging, isCounselling ? 1 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, isCounselling]);

  const TableHeader = () => (
    <TableHead>
      <TableRow>
        {COLUMN_PROMOTION.map((item) => (
          <TableCellFilted
            key={item.stateValue}
            label={item.label}
            stateValue={item.stateValue}
            style={{
              minWidth: item.minWidth || 'unset',
              width: item.width || 'unset',
            }}
            isHidden={false}
          />
        ))}
      </TableRow>
    </TableHead>
  );

  const renderTableBody = (row) => {
    return (
      <>
        <TableCell>
          <CustomTooltip content={row.code} />
        </TableCell>{' '}
        <TableCell>
          <CustomTooltip content={row.name} />
        </TableCell>
        <TableCell>
          <CustomTooltip content={row.quota} />
        </TableCell>
        <TableCell>{row.totalUsed}</TableCell>
        <TableCell>
          <CustomTooltip content={row.description} />
        </TableCell>
        <TableCell>{momentFormatted(row.startDate)}</TableCell>
        <TableCell>{momentFormatted(row.endDate)}</TableCell>
        <TableCell>
          <IconButton
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              setSelectedItem(row);
            }}
          >
            <MoreVertRounded />
          </IconButton>
        </TableCell>
      </>
    );
  };

  const listActions = [
    {
      key: 'edit-promotion',
      icon: EditIcon,
      label: `Edit ${isCounselling ? 'company code' : 'promo code'}`,
      styles: { width: 18, height: 18 },
      onClick: () => {
        setAnchorEl(null);
        onShowModal(TYPE_MODAL.Edit);
      },
    },
    {
      key: 'delete-promotion',
      icon: CloseCircle,
      label: `Delete ${isCounselling ? 'company code' : 'promo code'}`,
      styles: { width: 18, height: 18 },
      color: '#DB3D49',
      onClick: () => {
        setAnchorEl(null);
        setSelectedItem(selectedItem);
        setDeleteShowModal(true);
      },
    },
  ];

  return (
    <>
      <CustomMenu
        id="simple-menu"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <CustomMenuActions listActions={listActions} />
      </CustomMenu>
      <CustomPaperContainer
        header={
          <CustomHeader
            searchPlaceholder="Search by name..."
            title={getTitle()}
            setSearchKey={setSearchKey}
            searchKey={searchKey}
            fetchData={fetchData}
            renderButton={() => (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<CreateNewIcon />}
                  onClick={() => {
                    setSelectedItem(null);
                    onShowModal(TYPE_MODAL.Create);
                  }}
                >
                  Create new
                </Button>
              </>
            )}
          />
        }
      >
        <CustomTable
          data={data}
          paging={paging}
          header={TableHeader}
          renderRow={(row, i) => renderTableBody(row)}
          noDataHelperText={isCounselling ? 'No company code' : 'No promotion'}
          noDataHelperSubText={
            isCounselling
              ? 'Please create a new company code.'
              : 'Please create a new promotion.'
          }
          totalCols={COLUMN_PROMOTION.length}
          fetchData={fetchData}
          style={{ height: 'calc(100vh - 207px)' }}
          Icon={NoDataQueueClosureIcon}
        />
      </CustomPaperContainer>

      <Drawer
        anchor="right"
        open={showModal}
        onClose={onCloseModal}
        disableBackdropClick
        PaperProps={{
          style: {
            width: '30%',
            minWidth: 448,
          },
        }}
      >
        <ModalAddOrEditPromoCode
          onClose={onCloseModal}
          typeModal={typeModal}
          onSubmit={handleSubmitPromoCode}
          selectedItem={selectedItem}
          loading={loading}
          searchKey={searchKey}
          isCounselling={isCounselling}
        />
      </Drawer>

      {showDeleteModal && (
        <ModalConfirmation
          open={showDeleteModal}
          onClick={onDeletePromoCode}
          onClose={() => setDeleteShowModal(false)}
          mainContent={`Delete ${
            isCounselling ? 'company code' : 'promo code'
          }`}
          subContent={`Are you sure you want to delete this ${
            isCounselling ? 'company code' : 'promo code'
          }?`}
          cancelLabel="No"
          confirmLabel="Yes"
          confirmColor="red"
          Icon={<DeleteModalIcon />}
        />
      )}
    </>
  );
};

Promotion.propTypes = {
  type: PropTypes.string,
};

Promotion.defaultProps = {
  type: 'default',
};

export default Promotion;
